import { ATTENTION_INFO, attentionText } from '@tekansa/core/analytics';
import { CheckCircle2, ClipboardCheck } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useAttention } from '@/api/queries/home';
import { useCan } from '@/auth/permissions';
import { useActiveWorkspace } from '@/auth/workspace';
import { ATTENTION_ICONS } from '@/components/home/attention-icons';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Step 17c: "Things to fix" in the top bar, for people who can fix mailboxes: a round count of
 * what needs a person (the same list as Home's "Needs your attention"; warming-up mailboxes are
 * listed but never counted). Each line opens the page where it is fixed.
 */
export function AttentionBadge() {
  const canFix = useCan('mailboxes.edit');
  const ws = useActiveWorkspace().id;
  const data = useAttention(ws, canFix).data;
  const navigate = useNavigate();
  if (!canFix) return null;
  const count = data?.count ?? 0;
  const items = data?.items ?? [];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={count > 0 ? `Things to fix: ${count}` : 'Things to fix: none'}
          className="relative"
        >
          <ClipboardCheck aria-hidden="true" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 grid min-w-4 place-items-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Things to fix</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <p className="flex items-center gap-2 px-2 py-4 text-sm text-muted-foreground">
            <CheckCircle2 aria-hidden="true" className="size-4 text-chart-good" />
            All good. Nothing needs you right now.
          </p>
        ) : (
          items.map((item) => {
            const Icon = ATTENTION_ICONS[item.kind];
            const { text, action } = attentionText(item.kind, item.count);
            const info = ATTENTION_INFO.has(item.kind);
            return (
              <DropdownMenuItem
                key={item.kind}
                className="flex items-start gap-2.5 py-2"
                onSelect={() => void navigate(item.link)}
              >
                <Icon
                  aria-hidden="true"
                  className={info ? 'mt-0.5 text-primary' : 'mt-0.5 text-amber-600'}
                />
                <span className="min-w-0 flex-1 text-sm">{text}</span>
                <span className="shrink-0 text-xs font-medium text-primary">{action}</span>
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
